import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calculator, IndianRupee, TrendingUp, PiggyBank } from "lucide-react";
import { formatINRShort } from "@/utils/currency";

interface MortgageCalculatorProps {
  propertyPrice: number;
}

export function MortgageCalculator({ propertyPrice }: MortgageCalculatorProps) {
  const [downPayment, setDownPayment] = useState(Math.round(propertyPrice * 0.2)); // 20% default
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [interestRate, setInterestRate] = useState(8.5); // Current avg rate in India
  const [loanTerm, setLoanTerm] = useState(20); // years
  const [monthlyIncome, setMonthlyIncome] = useState(100000);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [loanAmount, setLoanAmount] = useState(0);

  // Calculate loan amount when down payment changes
  useEffect(() => {
    setLoanAmount(propertyPrice - downPayment);
  }, [propertyPrice, downPayment]);

  // Calculate EMI whenever inputs change
  useEffect(() => {
    if (loanAmount > 0 && interestRate > 0 && loanTerm > 0) {
      const monthlyRate = interestRate / 100 / 12;
      const numPayments = loanTerm * 12;
      
      const emi = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                  (Math.pow(1 + monthlyRate, numPayments) - 1);
      
      const totalPmt = emi * numPayments;
      const totalInt = totalPmt - loanAmount;
      
      setMonthlyEMI(Math.round(emi));
      setTotalPayment(Math.round(totalPmt));
      setTotalInterest(Math.round(totalInt));
    }
  }, [loanAmount, interestRate, loanTerm]);

  const handleDownPaymentChange = (value: string) => {
    const amount = parseInt(value) || 0;
    setDownPayment(amount);
    setDownPaymentPercent(Math.round((amount / propertyPrice) * 100));
  };

  const handleDownPaymentPercentChange = (value: string) => {
    const percent = parseFloat(value) || 0;
    setDownPaymentPercent(percent);
    setDownPayment(Math.round((percent / 100) * propertyPrice));
  };

  const emiToIncomeRatio = (monthlyEMI / monthlyIncome) * 100;
  const affordabilityStatus = emiToIncomeRatio <= 40 ? "Good" : emiToIncomeRatio <= 50 ? "Moderate" : "High Risk";
  const affordabilityColor = emiToIncomeRatio <= 40 ? "text-green-600" : emiToIncomeRatio <= 50 ? "text-yellow-600" : "text-red-600";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Mortgage Calculator
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="calculator" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="calculator">Calculator</TabsTrigger>
            <TabsTrigger value="affordability">Affordability</TabsTrigger>
          </TabsList>
          
          <TabsContent value="calculator" className="space-y-4">
            {/* Property Price */}
            <div className="space-y-2">
              <Label>Property Price</Label>
              <div className="text-2xl font-bold text-primary">
                {formatINRShort(propertyPrice)}
              </div>
            </div>

            {/* Down Payment */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="downPayment">Down Payment</Label>
                <Input
                  id="downPayment"
                  type="number"
                  value={downPayment}
                  onChange={(e) => handleDownPaymentChange(e.target.value)}
                  placeholder="Enter amount"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="downPaymentPercent">Percentage</Label>
                <Input
                  id="downPaymentPercent"
                  type="number"
                  value={downPaymentPercent}
                  onChange={(e) => handleDownPaymentPercentChange(e.target.value)}
                  placeholder="%"
                />
              </div>
            </div>

            {/* Interest Rate */}
            <div className="space-y-2">
              <Label htmlFor="interestRate">Interest Rate (% per annum)</Label>
              <Input
                id="interestRate"
                type="number"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                placeholder="8.5"
              />
            </div>

            {/* Loan Term */}
            <div className="space-y-2">
              <Label htmlFor="loanTerm">Loan Term (years)</Label>
              <Input
                id="loanTerm"
                type="number"
                value={loanTerm}
                onChange={(e) => setLoanTerm(parseInt(e.target.value) || 0)}
                placeholder="20"
              />
            </div>

            {/* Results */}
            <div className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-sm text-muted-foreground">Loan Amount</div>
                  <div className="text-lg font-semibold">{formatINRShort(loanAmount)}</div>
                </div>
                <div className="text-center p-3 bg-primary/10 rounded-lg">
                  <div className="text-sm text-muted-foreground">Monthly EMI</div>
                  <div className="text-lg font-semibold text-primary">{formatINRShort(monthlyEMI)}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-sm text-muted-foreground">Total Payment</div>
                  <div className="text-lg font-semibold">{formatINRShort(totalPayment)}</div>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <div className="text-sm text-muted-foreground">Total Interest</div>
                  <div className="text-lg font-semibold">{formatINRShort(totalInterest)}</div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="affordability" className="space-y-4">
            {/* Monthly Income */}
            <div className="space-y-2">
              <Label htmlFor="monthlyIncome">Monthly Income</Label>
              <Input
                id="monthlyIncome"
                type="number"
                value={monthlyIncome}
                onChange={(e) => setMonthlyIncome(parseInt(e.target.value) || 0)}
                placeholder="100000"
              />
            </div>

            {/* Affordability Analysis */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4" />
                <span className="font-medium">Affordability Analysis</span>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">EMI to Income Ratio</span>
                    <span className={`font-semibold ${affordabilityColor}`}>
                      {emiToIncomeRatio.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-background rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        emiToIncomeRatio <= 40 ? 'bg-green-500' : 
                        emiToIncomeRatio <= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(emiToIncomeRatio, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>Ideal: ≤40%</span>
                    <span>Status: <span className={affordabilityColor}>{affordabilityStatus}</span></span>
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Available Income After EMI</span>
                    <span className="font-semibold">{formatINRShort(monthlyIncome - monthlyEMI)}</span>
                  </div>
                </div>

                <div className="p-4 bg-primary/10 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <PiggyBank className="h-4 w-4 text-primary" />
                    <span className="font-medium">Recommendation</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {emiToIncomeRatio <= 40 
                      ? "This property is well within your budget. Consider investing the surplus in other assets."
                      : emiToIncomeRatio <= 50
                      ? "This property is manageable but will be a significant portion of your income. Ensure you have emergency funds."
                      : "This property may strain your finances. Consider a higher down payment or look for more affordable options."
                    }
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}